// src/client/game/core/tail/TailModel.ts

import type { GridPos } from "../types";
import type { TailSegment } from "./TailSegment";
import { SegmentSkin } from "./SegmentSkin";

/**
 * TailModel is a lightweight, engine-agnostic representation of the snake body.
 *
 * - index 0 is the head
 * - last index is the tail tip
 *
 * It stores segment metadata (skin/charge) but does not define gameplay.
 *
 * IMPORTANT:
 * - Do NOT call `setFromBody()` every tick if your body array is produced by
 *   unshift/pop operations; that will scramble metadata. Instead, use `step()`
 *   and `growAtTail()` when you wire this into the game loop (next step).
 */
export class TailModel<TCharge = unknown> {
  private segments: TailSegment<TCharge>[] = [];
  private nextId = 1;

  /** Initialize (or fully resync) from a head->tail body path. Metadata is reset. */
  setFromBody(body: GridPos[]) {
    this.segments = body.map((pos) => ({
      id: String(this.nextId++),
      pos,
      skin: SegmentSkin.Normal,
    }));
  }

  /** Advance positions by one move (head moves to newHead). Does not change length. */
  step(newHead: GridPos) {
    if (this.segments.length === 0) {
      this.segments.push({
        id: String(this.nextId++),
        pos: newHead,
        skin: SegmentSkin.Normal,
      });
      return;
    }

    // Shift positions from head -> tail (metadata stays attached to the index).
    for (let i = this.segments.length - 1; i >= 1; i--) {
      this.segments[i].pos = this.segments[i - 1].pos;
    }
    this.segments[0].pos = newHead;
  }

  /**
   * Grow by one segment at the tail tip (like standard snake growth).
   * The new segment starts at the current tail tip position.
   */
  growAtTail(opts?: { skin?: SegmentSkin; charge?: TCharge }) {
    const tailPos = this.segments.length > 0 ? this.segments[this.segments.length - 1].pos : { x: 0, y: 0 };
    this.segments.push({
      id: String(this.nextId++),
      pos: tailPos,
      skin: opts?.skin ?? SegmentSkin.Normal,
      charge: opts?.charge,
    });
  }

  /** Shrink to a new length (used later for "shrink" foods / laser cuts). */
  truncate(newLength: number) {
    if (newLength < 0) newLength = 0;
    if (newLength >= this.segments.length) return;
    this.segments.length = newLength;
  }

  get length() {
    return this.segments.length;
  }

  /** Ordered head -> tail. */
  getSegments(): readonly TailSegment<TCharge>[] {
    return this.segments;
  }

  getSegment(index: number): TailSegment<TCharge> | undefined {
    return this.segments[index];
  }

  get tailTipIndex(): number {
    return Math.max(0, this.segments.length - 1);
  }

  /** Set a purely visual skin hint on a segment. */
  setSkin(index: number, skin: SegmentSkin) {
    const seg = this.segments[index];
    if (seg) seg.skin = skin;
  }

  /**
   * Attach a charge to the segment. Intended to be used when eating special foods.
   * If skin is provided, it will be applied too.
   */
  setCharge(index: number, charge: TCharge, skin?: SegmentSkin) {
    const seg = this.segments[index];
    if (!seg) return;
    seg.charge = charge;
    if (skin) seg.skin = skin;
  }

  /**
   * Remove any charge from a segment. If a skin is provided, set it (e.g. Spent).
   */
  clearCharge(index: number, skin?: SegmentSkin) {
    const seg = this.segments[index];
    if (!seg) return;
    delete seg.charge;
    if (skin) seg.skin = skin;
  }

  /** Indices (head->tail) where a charge is present. */
  chargedIndices(): number[] {
    const out: number[] = [];
    for (let i = 0; i < this.segments.length; i++) {
      if (this.segments[i].charge != null) out.push(i);
    }
    return out;
  }
}
